import {
    getDocs,
    getCountFromServer,
    collection,
    query,
    where,
    doc,
    getDoc,
    updateDoc,
    addDoc,
    serverTimestamp,
    orderBy,
    limit,
    startAfter,
    documentId,
    Timestamp,
} from "firebase/firestore";
import { db, auth } from '../../../../firebase/config';
import dayjs from 'dayjs';

export type Survey = {
    id: string;
    surveyId?: string;
    createdBy: string;
    dateOfVisit?: string;
    branchId?: string;
    companyId?: string;
    locationId?: string;
    createdByName?: string;
    branch?: {
        id?: string;
        branchCode?: string;
        address?: string;
        branchName?: string;
        [key: string]: any;
    };
    companyDetails?: {
        companyName?: string;
        imgUrl?: string;
    };
    jointVisit?: string;
    jointTeams?: string[];
    cctv?: {
        status?: string;
        issues?: string[];
        otherIssues?: string[];
    };
    bams?: {
        status?: string;
        issues?: string[];
        otherIssues?: string[];
    };
    guarding?: {
        status?: string;
        issues?: string[];
        otherIssues?: string[];
    };
    customIssues?: Array<{
        heading?: string;
        description?: string;
    }>;
    status?: string;
    remarks?: string;
    createdAt?: any;
    updatedAt?: any;
    [key: string]: any;
};

export type Complaint = {
    id: string;
    complaintId?: string;
    surveyId?: string;
    createdBy: string;
    issueType?: string;
    branchId?: string;
    companyId?: string;
    surveyData?: Survey;
    status?: string;
    issues?: string[];
    resolvedIssues?: string[];
    customIssues?: Array<{
        heading?: string;
        description?: string;
    }>;
    description?: string;
    resolution?: string;
    targetCompanyName?: string;
    complaintCompanyDetails?: {
        companyName?: string;
    };
    createdAt?: any;
    updatedAt?: any;
    [key: string]: any;
};

async function fetchBankId(uid: string, isBankUser: boolean = false): Promise<string | null> {
    if (isBankUser) {
        // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
        const directBank = await getDoc(doc(db, "banks", uid));
        if (directBank.exists()) {
            return uid;
        }
        // Try query by userId field
        const banksSnap = await getDocs(collection(db, "banks"));
        for (const bankDoc of banksSnap.docs) {
            const bankData = bankDoc.data();
            if (bankData.userId === uid) {
                return bankDoc.id;
            }
        }
        return null;
    } else {
        // For PSO users, find which bank has this PSO
        const banksSnap = await getDocs(collection(db, "banks"));
        for (const bankDoc of banksSnap.docs) {
            const posDocRef = doc(db, "banks", bankDoc.id, "pos", uid);
            const posDoc = await getDoc(posDocRef);
            if (posDoc.exists()) {
                return bankDoc.id;
            }
        }
        return null;
    }
}

export async function fetchSurveys(isBankUser: boolean = false): Promise<Survey[]> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        console.log('No user ID found');
        return [];
    }

    try {
        // Get bankId/companyId first
        let companyId = await fetchBankId(uid, isBankUser);
        // Fallback: if treated as bank user (e.g., MIS incharge) but not found via bank path,
        // try PSO lookup to resolve the company id
        if (!companyId && isBankUser) {
            companyId = await fetchBankId(uid, false);
        }
        if (!companyId) {
            console.log('No bank found for user:', uid);
            return [];
        }
        const resolvedCompanyId: string = companyId;

        // Fetch surveys from banks/{companyId}/visit
        // Bank users (and MIS treated as bank by caller) should see all surveys (no createdBy filter)
        const baseCollection = collection(db, "banks", resolvedCompanyId, "visit");
        const q = isBankUser
            ? baseCollection
            : query(baseCollection, where("createdBy", "==", uid));
        const visitSnap = await getDocs(q);

        console.log(`📊 Found ${visitSnap.docs.length} surveys`);

        // Process each survey and fetch branch/company details
        const surveysList = await Promise.all(
            visitSnap.docs.map(async (d) => {
                const data = d.data();
                let branch = null;
                let companyDetails = null;

                // Fetch branch data
                if (data.branchId) {
                    try {
                        const branchDoc = await getDoc(
                            doc(db, "banks", resolvedCompanyId, "branch", data.branchId)
                        );
                        if (branchDoc.exists()) {
                            branch = { id: branchDoc.id, ...branchDoc.data() };
                        }
                    } catch (error) {
                        console.error('Error fetching branch:', error);
                    }
                }

                // Fetch company details efficiently
                if (data.companyId) {
                    try {
                        // Try to get from bank document first (fastest)
                        const bankDoc = await getDoc(doc(db, "banks", data.companyId));
                        if (bankDoc.exists()) {
                            const bankData = bankDoc.data();
                            companyDetails = {
                                companyName: bankData.companyName || "N/A",
                                imgUrl: bankData.imgUrl || ""
                            };
                        } else {
                            // Fallback to companies collection
                            const companyDoc = await getDoc(doc(db, "companies", data.companyId));
                            if (companyDoc.exists()) {
                                const companyData = companyDoc.data();
                                companyDetails = {
                                    companyName: companyData.companyName || "N/A",
                                    imgUrl: companyData.imgUrl || ""
                                };
                            } else {
                                companyDetails = {
                                    companyName: "N/A",
                                    imgUrl: ""
                                };
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching company details:', error);
                        companyDetails = {
                            companyName: "N/A",
                            imgUrl: ""
                        };
                    }
                }

                const surveyData = {
                    id: d.id,
                    ...data,
                    branch,
                    companyDetails,
                    // Status will be calculated based on complaints in the component
                    // Don't set default here, let it be calculated dynamically
                } as Survey;

                return surveyData;
            })
        );

        // Sort by creation date (newest first)
        surveysList.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
            return dateB.getTime() - dateA.getTime();
        });

        console.log('✅ Fetched Surveys:', surveysList.length);
        return surveysList;
    } catch (error) {
        console.error('Error fetching surveys:', error);
        return [];
    }
}

export async function fetchComplaints(isBankUser: boolean = false): Promise<Complaint[]> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        console.log('No user ID found');
        return [];
    }

    try {
        // Get bankId/companyId first
        let companyId = await fetchBankId(uid, isBankUser);
        // Fallback similar to fetchSurveys for MIS incharge treated as bank
        if (!companyId && isBankUser) {
            companyId = await fetchBankId(uid, false);
        }
        if (!companyId) {
            console.log('No bank found for user:', uid);
            return [];
        }
        const resolvedCompanyId: string = companyId;

        // First fetch all surveys to get survey IDs
        const surveys = await fetchSurveys(isBankUser);

        // Fetch complaints from all surveys in parallel
        const complaintPromises = surveys.map(async (survey) => {
            try {
                const complaintsSnap = await getDocs(
                    collection(db, "banks", resolvedCompanyId, "visit", survey.id, "complain")
                );
                return complaintsSnap.docs.map(complaintDoc => ({
                    doc: complaintDoc,
                    survey: survey
                }));
            } catch (error) {
                console.error(`Error fetching complaints for survey ${survey.id}:`, error);
                return [];
            }
        });

        // Wait for all complaint queries to complete
        const allComplaintDocs = await Promise.all(complaintPromises);
        const flatComplaintDocs = allComplaintDocs.flat();

        console.log(`📋 Found ${flatComplaintDocs.length} complaints`);

        // Process complaints
        const complaintsList: Complaint[] = flatComplaintDocs.map(({ doc: complaintDoc, survey }) => {
            const complaintData = complaintDoc.data();

            return {
                id: complaintDoc.id,
                surveyId: survey.id,
                surveyData: survey,
                // Explicitly map all complaint fields
                branchId: complaintData.branchId,
                companyId: complaintData.companyId,
                createdAt: complaintData.createdAt,
                createdBy: complaintData.createdBy,
                issueType: complaintData.issueType,
                issues: complaintData.issues || [],
                status: complaintData.status,
                targetCompanyName: complaintData.targetCompanyName,
                updatedAt: complaintData.updatedAt,
                customIssues: complaintData.customIssues || [],
                description: complaintData.description,
                resolution: complaintData.resolution,
                // Use targetCompanyName as company details (no additional fetching)
                complaintCompanyDetails: complaintData.targetCompanyName ? {
                    companyName: complaintData.targetCompanyName
                } : null,
                ...complaintData // Spread any additional fields
            } as Complaint;
        });

        // Sort by creation date (newest first)
        complaintsList.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
            return dateB.getTime() - dateA.getTime();
        });

        console.log('✅ Fetched Complaints:', complaintsList.length);
        return complaintsList;
    } catch (error) {
        console.error('Error fetching complaints:', error);
        return [];
    }
}

export async function updateSurvey(companyId: string, surveyId: string, updates: Partial<Survey>): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        throw new Error('User not authenticated');
    }

    try {
        const surveyRef = doc(db, "banks", companyId, "visit", surveyId);
        await updateDoc(surveyRef, {
            ...updates,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error('Error updating survey:', error);
        throw error;
    }
}

export async function updateComplaint(
    companyId: string,
    visitId: string,
    complaintDocId: string,
    updates: Partial<Complaint>
): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        throw new Error('User not authenticated');
    }
    try {
        const ref = doc(db, 'banks', companyId, 'visit', visitId, 'complain', complaintDocId);
        await updateDoc(ref, {
            ...updates,
            updatedAt: new Date(),
        });
    } catch (error) {
        console.error('Error updating complaint:', error);
        throw error;
    }
}

export async function getCompanyId(uid: string, isBankUser: boolean = false): Promise<string | null> {
    return await fetchBankId(uid, isBankUser);
}

export async function createSurvey(companyId: string, surveyData: Partial<Survey>): Promise<string> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        throw new Error('User not authenticated');
    }

    try {
        const surveyRef = collection(db, "banks", companyId, "visit");
        const newSurvey = {
            ...surveyData,
            createdBy: uid,
            companyId: companyId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };
        const docRef = await addDoc(surveyRef, newSurvey);
        return docRef.id;
    } catch (error) {
        console.error('Error creating survey:', error);
        throw error;
    }
}

export type SurveyDateFilter = 'all' | 'daily' | 'weekly' | 'monthly';
export type SurveyStatusFilter = 'pending' | 'partial' | 'resolved' | 'all';
export type SurveyPageCursor = {
    createdAtMs?: number;
    dateOfVisit?: string;
    surveyId?: string;
    offset?: number;
    id: string;
    page?: number;
};

export async function fetchSurveysPage(params: {
    isBankUser?: boolean;
    psoDesignation?: string;
    pageSize: number;
    searchTerm?: string;
    selectedPsoName?: string;
    selectedPsoId?: string;
    selectedLocationName?: string;
    selectedLocationId?: string;
    dateFilter?: SurveyDateFilter;
    createdFrom?: Date | null;
    createdTo?: Date | null;
    createdFromYmd?: string | null;
    createdToYmd?: string | null;
    statusFilter?: SurveyStatusFilter;
    cursor?: SurveyPageCursor | null;
}): Promise<{
    surveys: Survey[];
    cursor: SurveyPageCursor | null;
    hasMore: boolean;
    companyId: string | null;
    totalCount: number;
    totalPages: number;
}> {
    const uid = auth.currentUser?.uid;
    if (!uid) return { surveys: [], cursor: null, hasMore: false, companyId: null, totalCount: 0, totalPages: 0 };

    const isBankUser = !!params.isBankUser;
    const normalizedDesignation = (params.psoDesignation || '').replace(/_/g, ' ').toLowerCase();
    const isHead = normalizedDesignation === 'head' || normalizedDesignation === 'head pso';
    const isMIS = normalizedDesignation.includes('mis');

    let companyId = await fetchBankId(uid, isBankUser || isMIS);
    if (!companyId && (isBankUser || isMIS)) {
        companyId = await fetchBankId(uid, false);
    }
    if (!companyId) return { surveys: [], cursor: null, hasMore: false, companyId: null, totalCount: 0, totalPages: 0 };

    const pageSize = Math.max(1, Math.min(100, params.pageSize || 20));
    const searchTerm = (params.searchTerm || '').trim();
    const dateFilter = params.dateFilter || 'all';
    const statusFilter: SurveyStatusFilter = params.statusFilter || 'all';
    const visitRef = collection(db, "banks", companyId, "visit");

    // Resolve IDs for PSO and Location
    const selectedPsoId = params.selectedPsoId && params.selectedPsoId !== 'all' ? params.selectedPsoId : '';
    const selectedLocationId = params.selectedLocationId && params.selectedLocationId !== 'all' ? params.selectedLocationId : '';

    // Convert Date objects or presets to YYYY-MM-DD strings
    let dateOfVisitStart: string | null = null;
    let dateOfVisitEnd: string | null = null;

    if (params.createdFromYmd && params.createdToYmd) {
        dateOfVisitStart = params.createdFromYmd;
        dateOfVisitEnd = params.createdToYmd;
    } else if (params.createdFrom && params.createdTo) {
        dateOfVisitStart = dayjs(params.createdFrom).format('YYYY-MM-DD');
        dateOfVisitEnd = dayjs(params.createdTo).format('YYYY-MM-DD');
    } else {
        const todayStr = dayjs().format('YYYY-MM-DD');
        if (dateFilter === 'daily') {
            dateOfVisitStart = todayStr;
            dateOfVisitEnd = todayStr;
        } else if (dateFilter === 'weekly') {
            dateOfVisitStart = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
            dateOfVisitEnd = todayStr;
        } else if (dateFilter === 'monthly') {
            dateOfVisitStart = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
            dateOfVisitEnd = todayStr;
        }
    }

    const normalizeStatus = (raw: any): string => String(raw || '').trim().toLowerCase();
    const matchesStatusFilter = (raw: any): boolean => {
        const normalized = normalizeStatus(raw);
        if (statusFilter === 'all') return true;
        if (statusFilter === 'pending') return normalized === '' || normalized === 'pending' || normalized === 'n/a' || normalized === 'na';
        return normalized === statusFilter;
    };

    const shouldShowAll = isBankUser || isMIS || isHead;

    // Build query clauses
    const queryClauses: any[] = [];
    if (!shouldShowAll) {
        queryClauses.push(where("createdBy", "==", uid));
    } else if (selectedPsoId) {
        queryClauses.push(where("createdBy", "==", selectedPsoId));
    }

    if (selectedLocationId) {
        queryClauses.push(where("locationId", "==", selectedLocationId));
    }

    if (dateOfVisitStart) {
        queryClauses.push(where("dateOfVisit", ">=", dateOfVisitStart));
    }
    if (dateOfVisitEnd) {
        queryClauses.push(where("dateOfVisit", "<=", dateOfVisitEnd));
    }

    // Determine ordering
    let baseClauses: any[];
    let isOrderedByDate = false;

    if (dateOfVisitStart) {
        baseClauses = [...queryClauses, orderBy('dateOfVisit', 'desc'), orderBy(documentId(), 'desc')];
        isOrderedByDate = true;
    } else {
        baseClauses = [...queryClauses, orderBy('createdAt', 'desc'), orderBy(documentId(), 'desc')];
    }

    const term = searchTerm.toLowerCase();
    const norm = (v: string) => (v || '').toLowerCase().replace(/[^a-z0-9]/g, '');

    const startPage = Math.max(1, params.cursor?.page || 1);
    let scanCursor = params.cursor?.id ? params.cursor : null;

    const accepted: Survey[] = [];
    let hasMore = false;
    const maxBatchLoops = 6;

    const mapDocToSurvey = async (d: any): Promise<Survey> => {
        const data = d.data() as any;
        let branch = null;
        let createdByName = data.createdByName || '';
        let locationName = data.locationName || '';

        if (data.branchId) {
            const branchDoc = await getDoc(doc(db, "banks", companyId!, "branch", data.branchId));
            if (branchDoc.exists()) {
                const branchData = branchDoc.data() as any;
                if (!locationName && branchData.locationId) {
                    try {
                        const locationDoc = await getDoc(doc(db, "locations", String(branchData.locationId)));
                        if (locationDoc.exists()) {
                            locationName = String((locationDoc.data() as any)?.name || '').trim();
                        }
                    } catch {
                        // keep location name empty
                    }
                }
                branch = { id: branchDoc.id, ...branchData, locationName };
            }
        }

        if (!createdByName && data.createdBy) {
            try {
                const psoDoc = await getDoc(doc(db, "banks", companyId!, "pos", String(data.createdBy)));
                if (psoDoc.exists()) {
                    createdByName = String((psoDoc.data() as any)?.name || '').trim();
                }
            } catch {
                // keep empty name
            }
        }

        return {
            id: d.id,
            ...data,
            branch,
            createdByName,
            locationName,
        } as Survey;
    };

    let fallbackUsed = false;
    let loop = 0;

    while (accepted.length < pageSize && loop < maxBatchLoops) {
        loop += 1;
        let clauses: any[];
        
        if (fallbackUsed) {
            // Simple fallback: query everything for current user/company ordered by createdAt desc
            const fallbackClauses: any[] = [];
            if (!shouldShowAll) {
                fallbackClauses.push(where("createdBy", "==", uid));
            }
            fallbackClauses.push(orderBy('createdAt', 'desc'));
            fallbackClauses.push(orderBy(documentId(), 'desc'));
            if (scanCursor?.createdAtMs) {
                fallbackClauses.push(startAfter(Timestamp.fromMillis(scanCursor.createdAtMs), scanCursor.id));
            }
            fallbackClauses.push(limit(pageSize + 1));
            clauses = fallbackClauses;
        } else {
            clauses = [...baseClauses];
            if (scanCursor) {
                if (isOrderedByDate && scanCursor.dateOfVisit) {
                    clauses.push(startAfter(scanCursor.dateOfVisit, scanCursor.id));
                } else if (scanCursor.createdAtMs) {
                    clauses.push(startAfter(Timestamp.fromMillis(scanCursor.createdAtMs), scanCursor.id));
                }
            }
            clauses.push(limit(pageSize + 1));
        }

        let snap;
        try {
            snap = await getDocs(query(visitRef, ...clauses));
        } catch (err: any) {
            if (err.code === 'failed-precondition' || err.message?.includes('index')) {
                console.warn("Firestore index missing, falling back to client-side filtering. Error:", err.message);
                fallbackUsed = true;
                // reset cursor for fallback if ordering changes
                if (isOrderedByDate) {
                    scanCursor = null; 
                }
                loop -= 1; // retry with fallback
                continue;
            } else {
                throw err;
            }
        }

        if (snap.empty) {
            hasMore = false;
            break;
        }

        const docs = snap.docs;
        const batchHasMore = docs.length > pageSize;
        const pageDocs = batchHasMore ? docs.slice(0, pageSize) : docs;

        const mapped = await Promise.all(pageDocs.map((d: any) => mapDocToSurvey(d)));
        
        const matchesClientFilters = (survey: Survey): boolean => {
            if (!matchesStatusFilter(survey.status)) return false;
            
            // If fallback is used, we must filter on fields that were excluded from the query
            if (fallbackUsed) {
                if (selectedPsoId && survey.createdBy !== selectedPsoId) return false;
                if (selectedLocationId && survey.locationId !== selectedLocationId) return false;
                if (dateOfVisitStart && (!survey.dateOfVisit || survey.dateOfVisit < dateOfVisitStart)) return false;
                if (dateOfVisitEnd && (!survey.dateOfVisit || survey.dateOfVisit > dateOfVisitEnd)) return false;
            }

            if (!term) return true;

            const surveyId = String(survey.surveyId || '');
            const branchCode = String(survey.branch?.branchCode || '');
            const branchName = String(survey.branch?.branchName || survey.branch?.address || '');
            return (
                surveyId.toLowerCase().includes(term) ||
                branchCode.toLowerCase().includes(term) ||
                branchName.toLowerCase().includes(term) ||
                norm(surveyId).includes(norm(searchTerm)) ||
                norm(branchCode).includes(norm(searchTerm)) ||
                norm(branchName).includes(norm(searchTerm))
            );
        };

        for (const survey of mapped) {
            if (matchesClientFilters(survey)) {
                accepted.push(survey);
                if (accepted.length >= pageSize) break;
            }
        }

        const lastDoc = pageDocs[pageDocs.length - 1];
        const lastData = (lastDoc?.data() as any) || {};
        const lastCreatedAtMs = lastData.createdAt?.toMillis ? lastData.createdAt.toMillis() : 0;
        
        scanCursor = {
            createdAtMs: lastCreatedAtMs,
            dateOfVisit: lastData.dateOfVisit || '',
            id: lastDoc.id
        };

        hasMore = batchHasMore;
        if (!batchHasMore) break;
    }

    const totalCount = accepted.length;
    const nextCursor = hasMore && scanCursor
        ? { ...scanCursor, page: startPage + 1 }
        : null;
    const totalPages = hasMore ? startPage + 1 : startPage;

    return {
        surveys: accepted.slice(0, pageSize),
        cursor: nextCursor,
        hasMore,
        companyId,
        totalCount,
        totalPages,
    };
}

/** Walks paginated survey queries until all rows matching the same filters as `fetchSurveysPage` are loaded. */
export async function fetchAllSurveysForExport(
    params: Omit<Parameters<typeof fetchSurveysPage>[0], 'cursor' | 'pageSize'> & {
        pageSize?: number;
        onProgress?: (info: { loaded: number; pageIndex: number; hasMore: boolean }) => void;
        shouldAbort?: () => boolean;
    }
): Promise<Survey[]> {
    const { onProgress, shouldAbort, pageSize: pageSizeArg, ...pageParams } = params;
    const pageSize = Math.min(100, Math.max(1, pageSizeArg ?? 100));
    const all: Survey[] = [];
    let cursor: SurveyPageCursor | null = null;
    let pageIndex = 0;
    const maxPages = 5000;

    while (pageIndex < maxPages) {
        if (shouldAbort?.()) break;
        const res: Awaited<ReturnType<typeof fetchSurveysPage>> = await fetchSurveysPage({
            ...pageParams,
            pageSize,
            cursor,
        });
        pageIndex += 1;
        all.push(...res.surveys);
        onProgress?.({ loaded: all.length, pageIndex, hasMore: res.hasMore });
        if (!res.hasMore || !res.cursor) break;
        cursor = res.cursor;
    }

    return all;
}

